/**
 * SIM-SZENE LEIPZIG – Game State Management
 * Verwaltet Ressourcen, Runden, Flags und Spielfortschritt.
 */
import { createContext } from 'preact';
import { useContext, useReducer, useEffect } from 'preact/hooks';
import { balancing, calculateEffects, getAchievement, getGameOverMessage } from '../data/loader.js';

// Storage Key für localStorage
const STORAGE_KEY = 'sim-szene-leipzig-save';

/**
 * Initialer Spielzustand
 */
export function createInitialState() {
  return {
    // Ressourcen (0-100)
    geld: balancing.startValues.geld,
    nerven: balancing.startValues.nerven,
    credibility: balancing.startValues.credibility,
    
    // Spielfortschritt
    round: 1,
    currentScenarioId: null,
    
    // Gespielte Szenarien (für Wiederholungs-Cooldown)
    playedScenarios: [],
    
    // Phasen & Pfad (Entwicklungspfade, nicht Wachstumsstufen)
    phase: 1,
    decisionsInPhase: 0,
    currentPathId: null,
    
    // Flags für Szenario-Verkettung
    flags: {},
    
    // Spielstatus
    gameOver: false,
    gameOverCause: null,
    
    // Statistiken
    stats: {
      decisionsCount: 0,
      startTime: Date.now()
    }
  };
}

/**
 * Prüft Game-Over-Bedingungen
 * @param {object} state - Aktueller State
 * @returns {string|null} Todesursache oder null
 */
function checkGameOver(state) {
  const { geld, nerven, credibility } = state;
  const { gameOver: limits } = balancing;
  
  if (geld <= limits.geld.min) return 'geld';
  if (nerven <= limits.nerven.min) return 'nerven';
  if (credibility <= limits.credibility.min) return 'credibility';
  
  // Optional: Max-Grenzen (wenn aktiviert)
  if (limits.enforceMax) {
    if (geld >= limits.geld.max) return 'geld';
    if (nerven >= limits.nerven.max) return 'nerven';
    if (credibility >= limits.credibility.max) return 'credibility';
  }
  
  return null;
}

/**
 * Clampt einen Wert zwischen 0 und 100
 */
function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value));
}

/**
 * State Reducer
 */
function gameReducer(state, action) {
  switch (action.type) {
    case 'APPLY_EFFECTS': {
      const { effects, scenarioId } = action;
      const calculated = calculateEffects(effects, state.round);
      
      const newState = {
        ...state,
        geld: clamp(state.geld + calculated.geld),
        nerven: clamp(state.nerven + calculated.nerven),
        credibility: clamp(state.credibility + calculated.credibility),
        playedScenarios: [...state.playedScenarios, scenarioId],
        decisionsInPhase: (state.decisionsInPhase ?? 0) + 1,
        stats: {
          ...state.stats,
          decisionsCount: state.stats.decisionsCount + 1
        }
      };
      
      // Game Over prüfen
      const cause = checkGameOver(newState);
      if (cause) {
        return {
          ...newState,
          gameOver: true,
          gameOverCause: cause
        };
      }
      
      return newState;
    }
    
    case 'NEXT_ROUND': {
      return {
        ...state,
        round: state.round + 1,
        currentScenarioId: null
      };
    }
    
    case 'SET_SCENARIO': {
      return {
        ...state,
        currentScenarioId: action.scenarioId
      };
    }
    
    case 'SET_FLAGS': {
      return {
        ...state,
        flags: {
          ...state.flags,
          ...action.flags
        }
      };
    }
    
    case 'ADVANCE_PHASE': {
      const nextPhase = Math.min(3, (state.phase ?? 1) + 1);
      return {
        ...state,
        phase: nextPhase,
        decisionsInPhase: 0,
        ...(action.pathId != null && { currentPathId: action.pathId })
      };
    }
    
    case 'RESET_GAME': {
      return createInitialState();
    }
    
    case 'LOAD_GAME': {
      return action.savedState;
    }
    
    default:
      return state;
  }
}

/**
 * Game State Context
 */
const GameStateContext = createContext(null);

/**
 * Provider-Komponente für den Spielzustand
 */
export function GameStateProvider({ children }) {
  // Versuche gespeicherten Stand zu laden
  const loadedState = loadGameState();
  const [state, dispatch] = useReducer(gameReducer, loadedState || createInitialState());
  
  // Auto-Save bei Änderungen (außer bei Game Over)
  useEffect(() => {
    if (!state.gameOver) {
      saveGameState(state);
    }
  }, [state]);
  
  return (
    <GameStateContext.Provider value={{ state, dispatch }}>
      {children}
    </GameStateContext.Provider>
  );
}

/**
 * Hook zum Zugriff auf den Spielzustand
 */
export function useGameState() {
  const context = useContext(GameStateContext);
  if (!context) {
    throw new Error('useGameState must be used within a GameStateProvider');
  }
  return context;
}

// === Persistence ===

/**
 * Speichert den Spielzustand in localStorage
 */
function saveGameState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('Konnte Spielstand nicht speichern:', e);
  }
}

/**
 * Lädt den Spielzustand aus localStorage
 */
function loadGameState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (typeof parsed.geld === 'number' && typeof parsed.round === 'number') {
        return {
          ...parsed,
          phase: parsed.phase ?? 1,
          decisionsInPhase: parsed.decisionsInPhase ?? 0,
          currentPathId: parsed.currentPathId ?? null
        };
      }
    }
  } catch (e) {
    console.warn('Konnte Spielstand nicht laden:', e);
  }
  return null;
}

/**
 * Löscht den gespeicherten Spielstand
 */
export function clearGameState() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn('Konnte Spielstand nicht löschen:', e);
  }
}

/**
 * Prüft ob ein gespeicherter Spielstand existiert
 */
export function hasSavedGame() {
  return loadGameState() !== null;
}
